import { unwrap } from "@sagot/api-client";
import { DEFAULT_AUDIENCE_LABELS } from "@sagot/constants";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "./keys";
import type { AgentConfigOut, AgentSummaryOut, AudienceOut, BillingOverview } from "./types";

const SHARED_STALE_MS = 60_000;

/** `GET /v1/audiences`: the tenant's audience labels (Amendment A — never hard-coded copy). */
export function useAudiences(): UseQueryResult<AudienceOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.audiences(wsId), queryFn: () => unwrap(api.GET("/v1/audiences")), staleTime: SHARED_STALE_MS });
}

/** Label lookup by audience code; falls back to the code's default label while audiences load. */
export function useAudienceLabel(): (code: number) => string {
  const audiences = useAudiences();
  const data = audiences.data;
  return useCallback(
    (code: number) => data?.find((a) => a.code === code)?.label ?? (DEFAULT_AUDIENCE_LABELS as Readonly<Record<number, string | undefined>>)[code] ?? String(code),
    [data],
  );
}

export function useAgentsList(): UseQueryResult<AgentSummaryOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.agents(wsId), queryFn: async () => (await unwrap(api.GET("/v1/agents"))).items, staleTime: SHARED_STALE_MS });
}

export function useAgent(agentId: string | null | undefined): UseQueryResult<AgentConfigOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.agent(wsId, agentId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}", { params: { path: { agent_id: agentId ?? "" } } })),
    enabled: Boolean(agentId),
  });
}

/** `GET /v1/billing/overview` (storage quota, plan features). Optional: callers degrade when it fails (no billing.view). */
export function useBillingOverview(): UseQueryResult<BillingOverview> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.billing(wsId), queryFn: () => unwrap(api.GET("/v1/billing/overview")), staleTime: SHARED_STALE_MS, retry: false });
}
